import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { AdminUser, UpdateAdminUserPayload } from './types'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface UpdateAdminUserResponse {
  response: {
    user: AdminUser
    errors?: string[]
  }
}

export const useUpdateAdminUser = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation<
    UpdateAdminUserResponse,
    Error,
    { variables: UpdateAdminUserPayload }
  >({
    mutationFn: async ({ variables }) => {
      const { id, ...updateData } = variables

      const response = await fetchWithAuth(`${URL}/admin/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      })

      return response
    },
    onSuccess: (data, variables) => {
      const userId = variables.variables.id

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      queryClient.invalidateQueries({ queryKey: ['adminUser', userId] })
    },
  })

  // Return in the format expected by existing code
  return [
    async (params: { variables: UpdateAdminUserPayload }) => {
      return mutation.mutateAsync(params)
    },
    {
      loading: mutation.isPending,
      error: mutation.error,
    },
  ] as const
}
